import React from 'react';
import { Modal, Box, Typography, IconButton, Link, Tooltip } from '@mui/material';
import { Close, Flag, CheckCircle, CalendarToday, Schedule } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPriorityColor, getStatusColor, getTaskTypeColor, lightenColor } from './calendarColorUtils';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import AssigneeAvatarStack from '../../../shared/AssigneeAvatarStack';
import { formatDateWithOptionalTime, isDueDateOverdue } from '../../../shared/timeUtils';
import './TicketsModal.scss';

const TicketsModal = ({ open, onClose, tickets, date, colorCodeOption }) => {
  const { t } = useTranslation();
  const getDisplayValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.name || value.title || '';
  };

  const getTicketStyle = (ticket) => {
    if (!colorCodeOption) return {};
    
    let borderColor;
    if (colorCodeOption === 'status') {
      borderColor = getStatusColor(ticket.status);
    } else if (colorCodeOption === 'priority') {
      borderColor = getPriorityColor(ticket.priority);
    } else if (colorCodeOption === 'taskType') {
      borderColor = getTaskTypeColor(ticket.taskType);
    }
    
    if (borderColor) {
      const bgColor = lightenColor(borderColor);
      return {
        backgroundColor: bgColor,
        borderLeft: `4px solid ${borderColor}`
      };
    }
    return {};
  };

  const getIconColor = (ticket, type) => {
    if (!colorCodeOption) {
      if (type === 'priority') return getPriorityColor(ticket.priority);
      if (type === 'status') return getStatusColor(ticket.status);
      return undefined;
    }
    
    if (colorCodeOption === type) {
      if (type === 'priority') return getPriorityColor(ticket.priority);
      if (type === 'status') return getStatusColor(ticket.status);
      if (type === 'taskType') return getTaskTypeColor(ticket.taskType);
    }
    
    return type === 'priority' ? getPriorityColor(ticket.priority) : getStatusColor(ticket.status);
  };

  const isOverdue = (ticket) => {
    return isDueDateOverdue(ticket.dueDateRaw || ticket.dueDate, ticket.hasDueTime, ticket.completed);
  };

  // Re-render when tickets change (including completion status)
  const ticketKeys = tickets.map(t => `${t.id}-${t.completed}`).join(',');

  return (
    <Modal open={open} onClose={onClose}>
      <Box className="tickets-modal">
        <Box className="tickets-modal-header">
          <Typography className="tickets-modal-date">{date}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
        
        <Box className="tickets-modal-content" key={ticketKeys}>
          {tickets.map((ticket, index) => (
            <React.Fragment key={ticket.id}>
              <Box 
                className="ticket-item"
                style={getTicketStyle(ticket)}
              >
                <Box className="ticket-number-title">
                  <Box className="ticket-title-content">
                    <Link 
                      href={`/task/${ticket.ticketNumber}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ticket-number-link"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {ticket.ticketNumber}
                    </Link>
                    <span> - {ticket.title}</span>
                  </Box>
                  {isOverdue(ticket) && (
                    <Tooltip title={t('calendar.overdueTask')} arrow slotProps={{ tooltip: { className: 'app-tooltip' } }}>
                      <Schedule className="ticket-overdue-icon" />
                    </Tooltip>
                  )}
                </Box>
                
                <Box className="ticket-details">
                  <Box className="ticket-field">
                    <Flag sx={{ fontSize: 16, color: getIconColor(ticket, 'priority') }} />
                    <OverflowTooltip 
                      title={getDisplayValue(ticket.priority)} 
                      placement="top"
                      tooltipWidth={300}
                      className="ticket-text ticket-text-truncate"
                    >
                      {getDisplayValue(ticket.priority)}
                    </OverflowTooltip>
                  </Box>
                  <Box className="ticket-field">
                    <CalendarToday sx={{ fontSize: 16 }} />
                    <Typography className="ticket-text">
                      {(ticket.dueDateRaw || ticket.dueDate)
                        ? formatDateWithOptionalTime(ticket.dueDateRaw || ticket.dueDate, ticket.hasDueTime)
                        : t('calendar.noDueDate')}
                    </Typography>
                  </Box>
                  <Box className="ticket-field">
                    <CheckCircle sx={{ fontSize: 16, color: getIconColor(ticket, 'status') }} />
                    <OverflowTooltip 
                      title={getDisplayValue(ticket.status)} 
                      placement="top"
                      tooltipWidth={300}
                      className="ticket-text ticket-text-truncate"
                    >
                      {getDisplayValue(ticket.status)}
                    </OverflowTooltip>
                  </Box>
                  {(ticket.assignees?.length > 0 || ticket.assignee) ? (
                    <Box className="ticket-assignee">
                      <AssigneeAvatarStack assignees={ticket.assignees} assignee={ticket.assignee} size={20} />
                    </Box>
                  ) : (
                    <Box className="ticket-field" />
                  )}
                </Box>
              </Box>
              {index < tickets.length - 1 && <Box className="ticket-divider" />}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default TicketsModal;
